import Library from '../Library/Library';
import { dispatchTakeSeat } from './dispatchTakeSeat';
import GameAPI from './GameAPI';
import { DEFAULT_GAME, DEFAULT_GAME_PLAYER } from './GameDefaults';
import GameGroupHandler from './GameGroupHandler';
import GamePlayerController from './GamePlayers/GamePlayerController';


export default class Game {

    constructor(app) {
        this.app = app;
        this.authenticator = this.app.state.authenticator;
        this.userId = this.app.state.authenticator.account.user.id;
        this.gameApi = new GameAPI(this);
        this.gameData = {};

        this.groupHandler = new GameGroupHandler(this);

        this.gamePlayerControllers = [
            new GamePlayerController(this, 0),
            new GamePlayerController(this, 1),
            new GamePlayerController(this, 2),
            new GamePlayerController(this, 3),
            new GamePlayerController(this, 4),
            new GamePlayerController(this, 5),
        ];
        this.library = new Library(this);

        this.currentSeat = null;
        this.seatedController = null;
        this.sitting = false;
        // Phase C: lobbyData lets dispatchTakeSeat route pre-launch
        // seat claims through the REST endpoint. Populated by the
        // discord-lobby-joined window event the DiscordProvider fires.
        this.lobbyData = null;
        // The Discord identity captured at handshake-complete time —
        // used by dispatchTakeSeat to freeze a per-server nick onto
        // the Seat row when the user claims a seat. Null on open-web
        // and until the handshake fires.
        this.discord = null;

        window.addEventListener('game-event', this.gameEvent);
        window.addEventListener('game-player-event', this.gamePlayerEvent);
        window.addEventListener('game-pools-event', this.gamePoolsEvent);
        window.addEventListener('game-player-sit-event', this.gameSitEvent);
        window.addEventListener('patch-event', this.gamePatchEvent);
        window.addEventListener('reset-game-event', this.resetGame);
        window.addEventListener('take-seat-event', this.takeSeatEvent);
        window.addEventListener('discord-lobby-joined', this.lobbyJoinedEvent);
        window.addEventListener('discord-auth-complete', this.discordAuthEvent);
    }

    lobbyJoinedEvent = (e) => {
        this.lobbyData = (e.detail && e.detail.lobby_id) ? e.detail : null;
    }

    discordAuthEvent = (e) => {
        // The DiscordProvider fires `discord-auth-complete` with
        // `event.detail.userIdentity = backendResponse` — the raw
        // /api/session_auth/discord/ payload. Stash a minimal slice
        // matching the discord context shape so dispatchTakeSeat can
        // resolve nick → global_name → username without needing a
        // React-context bridge into this non-React class.
        const identity = (e.detail && e.detail.userIdentity) || null;
        if (!identity) {
            this.discord = null;
            return;
        }
        this.discord = {
            discordNick: identity.discord_nick || null,
            discordGlobalName: identity.discord_global_name || null,
            // discord_username is the real @handle from the identity
            // payload (SocialAccount.provider_username), NOT identity
            // .username (the synthetic discord_<snowflake> Django name).
            discordUsername: identity.discord_username || null,
        };
    }

    gameEvent = (e) => {
        // console.log('Game.gameEvent.')
        this.putGameAndPlayers(e.detail);
    }

    gamePatchEvent = (e) => {
        // console.log('Game.gamePatchEvent.')
        const receivedMessage = e.detail;
        let packageData = receivedMessage.package.data;
        let targetObject = receivedMessage.package.type;
        switch (targetObject) {
        case 'Game':
            console.log('not used at this time.')
            break;
        case 'Game.orbits':
            this.gameData.orbits = packageData;
            window.dispatchEvent(new CustomEvent('tradelanes-update-event', {
                detail: {
                    tradelanes: this.gameData.orbits,
                    status: this.gameData.status,
                }
            }));
            break;
        case 'Game.status':
            this.gameData.status = packageData;
            window.dispatchEvent(new CustomEvent('game-status-update-event', {
                detail: this.gameData.status
            }));
            break;
        default:
            break;

        }
    }

    takeSeatEvent = (e) => {
        const seat = e.detail;
        if (this.sitting === seat) {
            return;
        }
        this.sitting = seat;
        const result = dispatchTakeSeat({
            seat,
            lobbyData: this.lobbyData,
            gameData: this.gameData,
            gameApi: this.gameApi,
            groupHandler: this.groupHandler,
            gameController: this.app && this.app.gameController,
            discord: this.discord,
        });
        if (result === 'claim_seat' || result === 'noop') {
            // Pre-launch paths have no Sit/Join roundtrip to clear
            // this.sitting — reset so the user can re-claim or change
            // seats without being deduped.
            this.sitting = null;
        }
    }

    gameSitEvent = (e) => {
        console.log('Game.gameSitEvent');
        const gamePlayerData = e.detail;
        const seat = gamePlayerData.seat;
        // only requester receives sit event.
        this.currentSeat = seat;
        this.sitting = null;
        this.seatedController = this.gamePlayerControllers[seat];
        window.dispatchEvent(
            new CustomEvent('seated-game-player-event',
                { detail: gamePlayerData }));
    }

    gamePlayerEvent = (e) => {
        const gamePlayerData = e.detail;
        if (gamePlayerData.seat === this.currentSeat) {
            window.dispatchEvent(
                new CustomEvent('seated-game-player-event',
                    { detail: gamePlayerData }));
        }

        let playerIndex = this.gameData.gameplayer_set.findIndex(
            gamePlayer => gamePlayer.seat === gamePlayerData.seat);
        let newGameData = { ...this.gameData };
        newGameData.gameplayer_set[playerIndex] = gamePlayerData;
        this.putGameAndPlayers(newGameData);
    }

    gamePoolsEvent = (e) => {
        const pools = e.detail;
        if (!pools) return;
        // Merge the Game-level pool stacks the backend signaled onto the
        // local gameData. The per-seat game-player-event only replaces a
        // single gameplayer entry (Game.js shallow-clones gameData and
        // never touches the top-level pool arrays), so without this the
        // pools stayed frozen since the last full game broadcast — and
        // the client-side AI, which reads gamePlayerController.gameData
        // for availability, kept re-picking emptied faction stacks.
        // Routed through putGameAndPlayers so its JSON-equality guard
        // suppresses no-op repaints, and so each GamePlayerController
        // refreshes its gameData reference (see its
        // gamePlayerEventListener) to the freshly-pooled snapshot.
        const newGameData = { ...this.gameData };
        if (pools.action_pool) newGameData.action_pool = pools.action_pool;
        if (pools.amenity_pool) newGameData.amenity_pool = pools.amenity_pool;
        if (pools.bed_pool) newGameData.bed_pool = pools.bed_pool;
        if (pools.lodging_pool) newGameData.lodging_pool = pools.lodging_pool;
        this.putGameAndPlayers(newGameData);
    }

    putGameAndPlayers = (gameData) => {
        if (JSON.stringify(this.gameData) === JSON.stringify(gameData)) return;
        this.gameData = gameData;
        window.dispatchEvent(
            new CustomEvent(
                'game-event',
                { detail: this.gameData }));
        gameData.gameplayer_set.forEach(
            gamePlayerData => {
                window.dispatchEvent(
                    new CustomEvent(
                        'game-player-event',
                        { detail: gamePlayerData }));
            });
    }

    automaticPlayerIsEngaged = () => {
        return this.seatedController && this.seatedController.artificialPlayer.speedController.isEngaged();
    }

    gameOverPusher = () => {
        console.log('[postgame-fp] Game.gameOverPusher is_simulation=%s',
            this.gameData && this.gameData.is_simulation);
        if (this.gameData.is_simulation) {
            setTimeout(this.gameApi.getSimulation, 10);
            return;
        }
        // 2026-05-26: Post-game flow keeps the player in the SAME
        // lobby. Send EndGameSession (soft exit — backend clears
        // game/game_player on the connection, preserves the lobby
        // binding, and flips Lobby COMPLETED→FORMING on the first
        // dismisser, broadcasting Members).
        //
        // CC_FE_PAGES (2026-06-01): navigation is SUPPRESSED. The user
        // is already on `/game/:lobbyUuid` (the board), and GamePage
        // re-branches board→Lobby in place when that Members broadcast
        // lands with status FORMING. The old '/play/casual' target was
        // stale anyway (that path resolved to the Main Menu, not the
        // lobby). Explicit lobby exit (the Leave Lobby button / Esc-key
        // returnToMenu) still goes through
        // groupHandler.leaveGameGroup → gameApi.leaveGroup → backend
        // leave_group, which destroys-and-disperses peers when the
        // owner is the one leaving.
        this.gameApi.endGameSession();
    }

    resetGame = () => {
        const newGame = { ...DEFAULT_GAME };
        this.currentSeat = null;
        this.seatedController = null;
        window.dispatchEvent(
            new CustomEvent('game-event', { detail: newGame }));
    }

    returnToMenu = (currentRoute) => {
        this.groupHandler.leaveGameGroup();
        if (currentRoute !== '/') {
            window.dispatchEvent(
                new CustomEvent('quick-router-event', { detail: '/' }));
        }
    }

    deleteGame = () => {
        this.gameApi.delete();
    }


}
